// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A player who takes their headset off, and the avatar that must not flicker (M8-06).
//
// `web-windowed` and `webxr` are the *same browser* in two modes, and 06 §4 draws them as two
// completely different things: a headset player is a head, a visor and two hands, and a windowed
// player is a glowing orb with their name on it. So the moment a player's client kind changes, the
// thing standing at their bay changes shape in front of everybody.
//
// **That change is not rare and it is usually not a decision.** A Quest ends the immersive session
// whenever the system menu opens, whenever the headset comes off a face for a few seconds, and
// whenever the browser loses focus; the session is re-entered the moment they come back. Each
// round trip is an `xrsession` end and start, and each one, taken literally, is a person
// dissolving into an orb and back again. It is the visual equivalent of a status light that
// flickers every time somebody walks past.
//
// WHAT IS DEBOUNCED, AND WHAT IS NOT
// ----------------------------------
// **Only the downgrade waits.** Going *to* a headset is a deliberate act with a loading screen in
// front of it — nobody enters immersive mode by accident, and making them wait four seconds to be
// embodied would be four seconds of watching their own hands not appear. Going *from* one is
// usually an interruption. So an upgrade is instant and a downgrade is held, which is the same
// asymmetry a good connection indicator uses: go green immediately, go red reluctantly.
//
// **And it is enforced where the avatar is drawn, not where the kind is announced.** The rows
// being drawn are written by *other people's clients*: a build a version behind, a native client
// with different session semantics, a browser that crashed out of immersive without saying
// anything. A rule that only holds when every client cooperates is not a rule, so the client that
// has to look at the flicker is the one that suppresses it.
//
// **This client's own half of M8-06 is a different mechanism, in `arcadePresence.ts`.** A browser
// leaving immersive does not announce a new kind — it leaves the arcade altogether — so the thing
// to hold back there is the teardown, not a field. Same four seconds, same asymmetry, different
// verb.
//
// **It changes the kind and nothing else.** A player who takes their headset off is still standing
// at their bay, still holding it, still in the roster and still someone the bay resolver has to
// place. The only question this answers is what shape to draw them as.

import { type ClientKind } from './venuePresence'

/**
 * How long a downgrade is held before it is believed, milliseconds.
 *
 * Four seconds. Long enough to cover the interruptions that are not decisions — the system menu,
 * a headset lifted to look at something, a focus change — and measured in that order: a Quest's
 * menu round trip is comfortably under two seconds and a headset lifted off a face and put back
 * on is two to three. Short enough that a player who has genuinely gone back to their desk turns
 * into an orb before anybody wonders why their avatar is standing perfectly still.
 *
 * It is a *presentation* delay and never a correctness one: the roster, the bays and the pose
 * stream all see the real kind the instant it changes.
 */
export const DOWNGRADE_AFTER_MS = 4000

/**
 * Is this kind drawn as a body?
 *
 * The distinction the avatar cares about, which is not the same as the three the wire carries:
 * `webxr` and `quest-native` are both a person with a head and hands, and `web-windowed` is an
 * orb. Written as one predicate so the two clients cannot come to different conclusions about a
 * kind that is added later — a fourth embodied kind would be drawn correctly by both and
 * debounced correctly by both without either being edited.
 */
export function isEmbodied(kind: ClientKind): boolean {
  return kind !== 'web-windowed'
}

/**
 * Whether moving from one kind to another is the change that has to wait.
 *
 * Embodied to windowed, and only that. Windowed to embodied is an upgrade, embodied to embodied is
 * a player moving between a browser headset and the native client — which is a real change of
 * client but not a change of *shape*, so there is nothing to hide and nothing to wait for.
 */
export function isDowngrade(from: ClientKind, to: ClientKind): boolean {
  return isEmbodied(from) && !isEmbodied(to)
}

/** What one participant is currently drawn as, and what they have actually said. */
interface Held {
  /** The kind being reported to the renderer right now. */
  readonly shown: ClientKind
  /** The kind the wire last carried, which may not be `shown` yet. */
  readonly announced: ClientKind
  /** When the pending downgrade started, or `undefined` when nothing is pending. */
  readonly since: number | undefined
}

/**
 * The kind each participant is drawn as, held steady across a flap.
 *
 * One instance per session, keyed by participant. Deliberately **not** a member of `VenueRoster`:
 * the roster is a pure function of the rows the server sent and has no clock, and giving it one
 * would mean every question it answers — who is here, who is at bay 3 — depended on when it was
 * asked. This holds the clock, answers exactly one question, and is handed the time by its caller
 * so the whole of it is decidable in a test.
 */
export class PlatformSwitch {
  private readonly held = new Map<string, Held>()

  private readonly waitMs: number

  constructor(waitMs = DOWNGRADE_AFTER_MS) {
    this.waitMs = waitMs
  }

  /**
   * The kind to draw this participant as, given what they are claiming now.
   *
   * Call once per participant per frame with a monotonic `nowMs`. An upgrade, a first sighting and
   * a sideways move are all returned immediately; a downgrade returns the old kind until the wait
   * has elapsed, and returns the new one from then on.
   *
   * **Going back up cancels a pending downgrade outright rather than pausing it.** A player who
   * flickers in and out every three seconds would otherwise accumulate their way past the
   * threshold and turn into an orb during a moment they were wearing the headset — which is the
   * one outcome this exists to prevent, arrived at slowly.
   */
  kindOf(participantId: string, announced: ClientKind, nowMs: number): ClientKind {
    const previous = this.held.get(participantId)
    if (previous === undefined) {
      // First sighting. Whatever they say they are, they are — there is no earlier state to
      // protect and holding a newcomer as something they never claimed would be an invention.
      this.held.set(participantId, { shown: announced, announced, since: undefined })
      return announced
    }
    if (!isDowngrade(previous.shown, announced)) {
      this.held.set(participantId, { shown: announced, announced, since: undefined })
      return announced
    }
    // A downgrade. `since` is the moment it was *first* claimed, so the clock does not restart
    // every frame the same claim is repeated — which it is, fifteen times a second.
    const since = previous.since ?? nowMs
    if (nowMs - since >= this.waitMs) {
      this.held.set(participantId, { shown: announced, announced, since: undefined })
      return announced
    }
    this.held.set(participantId, { shown: previous.shown, announced, since })
    return previous.shown
  }

  /** True while this participant is claiming one thing and being drawn as another. */
  pending(participantId: string): boolean {
    return this.held.get(participantId)?.since !== undefined
  }

  /**
   * Forget everyone not in this set.
   *
   * Called with the current roster so a player who leaves does not sit in the map for the rest of
   * the session — and, more usefully, so a player who leaves *during* a pending downgrade and
   * comes back later is a first sighting rather than a four-second-old grudge.
   */
  retain(participantIds: Iterable<string>): void {
    const alive = new Set(participantIds)
    for (const id of [...this.held.keys()]) {
      if (!alive.has(id)) this.held.delete(id)
    }
  }

  /** How many participants are being tracked. For the leak test, which counts things. */
  get size(): number {
    return this.held.size
  }
}
